import { Info } from 'lucide-react';
import React from 'react';
import type { PercentileLevel } from '@talentmesh/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/shadcn';
import { cn, percentileInTextColors } from '@/lib/utils';

export interface PersonalityCharacteristicsToolTipProps {
    children?: React.ReactNode;
    hidden?: boolean;
    percentileScore: number;
    percentileLevel: PercentileLevel;
}

const PersonalityCharacteristicsToolTip = ({
    children,
    hidden,
    percentileScore,
    percentileLevel,
}: PersonalityCharacteristicsToolTipProps): React.JSX.Element => {
    const clampedScore = Math.min(100, Math.max(0, percentileScore));

    const content = (
        <div className="flex flex-row items-center justify-between gap-4">
            <div className="bg-muted h-2 w-full rounded-full">
                <div
                    className={cn(
                        'h-full rounded-full bg-current transition-all',
                        percentileInTextColors[percentileLevel],
                    )}
                    style={{ width: `${clampedScore}%` }}
                />
            </div>
            <Info
                className={cn(
                    'size-5 shrink-0',
                    percentileInTextColors[percentileLevel],
                    hidden ? 'invisible' : 'visible',
                )}
            />
        </div>
    );

    if (hidden) {
        return content;
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button type="button" className="w-full text-left" aria-label="Show characteristic aspects">
                        {content}
                    </button>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    align="center"
                    sideOffset={8}
                    hideArrow
                    className="bg-card text-foreground border-border border px-3 py-4 drop-shadow"
                >
                    {children}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default PersonalityCharacteristicsToolTip;
